import { BlockColumn } from '../../components/blocks/BlockColumn';
import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  AggregatedProfitLossInfo,
  AssetType,
  Balances,
  BalanceTotals,
  BasicSymbolProfitLossInfo,
  ProfitLossInfoMap,
} from '@tradingblock/types';
import _ from 'lodash';
import { accountBalances, useStateSelector } from '../../data/global/dataSelectors';
import { SubAccountFilter } from '../shared/SubAccountFilter';
import { useDispatch, useSelector } from 'react-redux';
import Config from '../../config';
import { account } from '../../data/global/selectors/accountSelectors';
import { AccountApiFactory, PartialQuote, PositionDTO, TodaysFilledOrders } from '@tradingblock/api';
import { PositionPerformanceWithPnL, QuoteSymbolQuotes } from '../../data/global/state';
import { aggregatedSymbolPosition, QuoteDataToPartialQuote } from '../../data/global/selectors/transforms';
import { getCashAndCashAlts } from '../../data/global/utilities/balances';
import { pnlSelectors } from '../../data/global/selectors/PnLSelectors';
import { SymbolPnLReducer } from '../../data/global/sliceReducers/performance/performanceBySymbolReducer';
import { UIActions } from '../../data/global/actions';
import { SymbolPosition, SymbolPositions } from '../../types';
import { positionToInfo } from '../../utilities/position';
import { useSharedPLCalculations } from '../../hooks/useSharedPLCalculations';

export const AccountBalanceView: FC<{ totals?: BalanceTotals }> = ({ totals }) => {
  // variables for building api client
  const accountId = useSelector(account.accountId);
  const apiRootUrl = Config.tradingApi;
  const token = useStateSelector(state => state.auth.apiToken) || undefined;

  // redux states
  const subId = useStateSelector(state => state.account.subAccountId);
  const quotes = useStateSelector(state => state.quotes.quoteMetadata.data);
  const positionValue = useStateSelector(s => s.performance.totals.positionValue);
  const loaded = useStateSelector(pnlSelectors.pnlDataLoaded);
  const quoteState = useStateSelector(state => state.quotes);
  const orders = useStateSelector(state => state.account.orders);
  const hasSubAccounts = useStateSelector(
    s => s.account.subaccounts.subaccounts && s.account.subaccounts.subaccounts.length > 0
  );
  const accountValue = useStateSelector(s => s.performance.totals.accountValue);
  const subAccountFilterId = useStateSelector(s => s.ui.dashboard.subAccountFilterId);
  const positions = useStateSelector(state => state.positions.positions);
  const subAccountBalances = useStateSelector(s => s.accountData.subAccountsBalances);
  const masterSubAccount = useStateSelector(
    state => state.account.subaccounts.subaccounts && state.account.subaccounts.subaccounts.find(s => s.IsMaster)
  );
  const isFetchingBalances = useStateSelector(accountBalances.isFetching);
  const isFetchingPositions = useStateSelector(s => s.positions.isFetching);

  const dispatch = useDispatch();

  // useStates for handling sub account filter
  const [selectedSubAccountId, setSelectedSubAccountId] = useState<number | undefined>(undefined);
  const [selectedSubAccountBalances, setSelectedSubAccountBalances] = useState<Balances | undefined>(undefined);
  const [selectedSubAccountPositions, setSelectedSubAccountPositions] = useState<PositionDTO[]>([]);
  const [selectedSubAccountCashValue, setSelectedSubAccountCashValue] = useState<number | undefined>(undefined);
  const [selectedSubAccountPositionValue, setSelectedSubAccountPositionValue] = useState<number | undefined>(undefined);
  const [selectedSubAccountTotalValue, setSelectedSubAccountTotalValue] = useState<number | undefined>(undefined);
  const [positionsRequested, setPositionsRequested] = useState(false);
  const [selectedSubAccountDailyPNL, setSelectedSubAccountDailyPNL] = useState<number | undefined>(undefined);
  const [selectedSubAccountTradingPNL, setSelectedSubAccountTradingPNL] = useState<number | undefined>(undefined);

  // Add this selector
  const performanceState = useStateSelector(state => state.performance.positions);

  const quotesArray = Object.values(quotes);

  const selectedAccountId = useStateSelector(state => state.account.accountId);

  const { dailyPL, isLoading } = useSharedPLCalculations();

  useEffect(() => {
    if (!subAccountFilterId) {
      setSelectedSubAccountId(undefined);
      setSelectedSubAccountBalances(undefined);
      setSelectedSubAccountPositions([]);
      setSelectedSubAccountPositionValue(undefined);
      setSelectedSubAccountCashValue(undefined);
      setSelectedSubAccountTotalValue(undefined);
      setSelectedSubAccountDailyPNL(undefined);
      setSelectedSubAccountTradingPNL(undefined);
    }
  }, [subAccountFilterId]);

  const setSubAccountId = async (subAccountId?: number) => {
    if (subAccountId) {
      if (subAccountId === -1) {
        setSelectedSubAccountId(undefined);
      } else {
        setSelectedSubAccountId(subAccountId);
      }
      setSelectedSubAccountPositions([]);
      setSelectedSubAccountCashValue(undefined);
      setSelectedSubAccountPositionValue(undefined);
      setSelectedSubAccountTotalValue(undefined);
      setSelectedSubAccountDailyPNL(undefined);
      setSelectedSubAccountTradingPNL(undefined);
      setSelectedSubAccountBalances(undefined);
      setPositionsRequested(false);
    }
  };

  const accountApi = useMemo(() => {
    if (accountId && token && !selectedSubAccountId) {
      return AccountApiFactory(apiRootUrl, token, accountId);
    }
    if (accountId && token && selectedSubAccountId === -1) {
      return AccountApiFactory(apiRootUrl, token, accountId);
    }
    if (accountId && apiRootUrl && token && selectedSubAccountId) {
      return AccountApiFactory(apiRootUrl, token, accountId, selectedSubAccountId);
    }
  }, [selectedSubAccountId, accountId, apiRootUrl, token]);

  const getSubAccountPositions = async () => {
    if (accountApi && selectedSubAccountId && selectedSubAccountPositions.length === 0 && !positionsRequested) {
      const positions = await accountApi.positions();
      setSelectedSubAccountPositions(positions.payload);
      setPositionsRequested(true);
      return positions.payload;
    }
  };

  const getSubAccountBalances = async () => {
    if (accountApi && selectedSubAccountId && !selectedSubAccountBalances) {
      const balances = await accountApi.balances();
      setSelectedSubAccountBalances(balances.payload);
      return balances.payload;
    }
  };

  const getSubAccountPositionQuotes = async (quotes: QuoteSymbolQuotes, positions: PositionDTO[]) => {
    const uniquePositions = _(positions)
      .map(p => [p.Symbol, p.UnderlyingSymbol])
      .flatMap()
      .uniq()
      .value();
    const quotesData = _.filter(quotes, (v, k: string) => uniquePositions.includes(k));
    const partialQuote = QuoteDataToPartialQuote(quotesData);
    return partialQuote;
  };

  const getSubAccountPositionValue = async (positions: PositionDTO[], subQuotes: PartialQuote[]) => {
    const positionValue = _(positions)
      .map(p => aggregatedSymbolPosition([p], subQuotes, p.UnderlyingSymbol).value)
      .sum();

    return positionValue;
  };

  const getSubAccountCashValue = async (balances: Balances) => {
    const cashValue = getCashAndCashAlts(balances);
    return cashValue;
  };

  const getAggregatedTotals = async (pnlMap: ProfitLossInfoMap) =>
    _.reduce(
      pnlMap,
      (acc: AggregatedProfitLossInfo, v: BasicSymbolProfitLossInfo): AggregatedProfitLossInfo => {
        const trading = acc.trading + v.trading;
        const position = acc.position + v.position;
        const total = trading + position;
        return {
          pnls: [...acc.pnls, v],
          trading,
          position,
          total,
        };
      },
      { trading: 0, position: 0, total: 0, pnls: [] }
    );

  const getSubAccountPositionsFromSymbol = async (positions: PositionDTO[]) => {
    const subPositions = _.reduce(
      positions,
      (acc: SymbolPositions, pos: PositionDTO): SymbolPositions => {
        // To make sure that options and common stock fall under the same category, group it buy the underlying symbol,
        // otherwise, if grouped by symbol, the symbols wont match (options have an id in the symbol name while common stock don't)
        const underlyingSymbol = pos.UnderlyingSymbol;
        const DEFAULTVALUE = { shares: 0, value: 0, positions: [], symbol: underlyingSymbol, options: 0 };
        let symbolValue = acc[underlyingSymbol] || DEFAULTVALUE;
        // If the accumulated object doesn't have a symbol, assign it default values.
        // if (!acc.hasOwnProperty(sym)) Object.assign({ [sym]: [pos], symbol: sym }, acc);
        // If the AssetType is common stock
        const isEquity = pos.AssetType === AssetType.Equity;
        // Aggregate the shares and values of the stock
        return {
          ...acc,
          [underlyingSymbol]: {
            ...symbolValue,
            shares: isEquity ? symbolValue.shares + pos.OpenQuantity : symbolValue.shares,
            options: isEquity ? symbolValue.options : symbolValue.options + pos.OpenQuantity,
            // value: symbolValue.value + pos.OpenPrice,
            positions: [...symbolValue.positions, pos],
          },
        };
      },
      {}
    );
    return subPositions;
  };

  const getSubAccountPositionsWithPerformance = async (subPositions: SymbolPositions, subQuotes: PartialQuote[]) => {
    const positionsWithPerformance = _.reduce(
      subPositions,
      (res, pos: SymbolPosition, key: string): SymbolPositions => {
        const posIds = _.map(pos.positions, p => p.PositionId);

        const aggregatedPnl = _.reduce(
          performanceState,
          (acc, pp, key) => {
            if (!posIds.includes(_.toString(key))) {
              return acc;
            }
            return {
              pnl: acc.pnl + pp.pnl,
            };
          },
          { pnl: 0 }
        );

        const curr: PositionPerformanceWithPnL = {
          ...aggregatedSymbolPosition(pos.positions, subQuotes, key),
          pnl: aggregatedPnl.pnl,
        };
        return {
          ...res,
          [key]: curr,
        };
      },
      {}
    );
    return _.map(positionsWithPerformance, (v, k) => positionToInfo(v));
  };

  const getSubAccountPositionsAndQuotes = async () => {
    if (accountApi && selectedSubAccountId && subAccountFilterId) {
      const positionResponse =
        selectedSubAccountPositions && selectedSubAccountPositions.length > 0
          ? selectedSubAccountPositions
          : await getSubAccountPositions();

      const balancesResponse = selectedSubAccountBalances || (await getSubAccountBalances());

      if (positionResponse && balancesResponse) {
        const subQuotes = await getSubAccountPositionQuotes(quotes, positionResponse);
        const positionValue = await getSubAccountPositionValue(positionResponse, subQuotes);
        const cashValue = await getSubAccountCashValue(balancesResponse);
        const totalValue = positionValue + cashValue;

        setSelectedSubAccountPositionValue(positionValue);
        setSelectedSubAccountCashValue(cashValue);
        setSelectedSubAccountTotalValue(totalValue);

        const subPositions = await getSubAccountPositionsFromSymbol(positionResponse);
        const subData = await getSubAccountPositionsWithPerformance(subPositions, subQuotes);

        const todaysOrders =
          orders &&
          TodaysFilledOrders(orders).filter(o =>
            selectedSubAccountId !== -1 ? o.SubaccountId === selectedSubAccountId : o
          );

        const bySymbol = todaysOrders && SymbolPnLReducer(quoteState, todaysOrders, positionResponse);
        const all = bySymbol && (await getAggregatedTotals(bySymbol));

        if (subData) {
          // Calculate total P/L from subData
          const totalPnl = subData.reduce((totalPnl, positionInfo) => {
            return totalPnl + (positionInfo.pnl || 0);
          }, 0);
          setSelectedSubAccountDailyPNL(totalPnl + ((all && all.trading) || 0));
        }

        if (all) {
          setSelectedSubAccountTradingPNL(all.trading);
        }
      }
    }
  };

  // This is put into place to refresh the Balance block when there is a change to the subaccount balances/positions.
  useEffect(() => {
    // This only needs to refresh when there is a selectedSubAccountId
    if (selectedSubAccountId) {
      const tempSubId = selectedSubAccountId;
      // SelectedSubAccount is the master (Assume either master or related subaccounts has changed)
      // Always refresh
      if (masterSubAccount && masterSubAccount.Id === tempSubId && subAccountBalances) {
        setSubAccountId(-1);
        setTimeout(() => setSubAccountId(tempSubId), 500);
      } else if (
        // if there is a change between the current block values and the new subAccountBalance cash values
        // refresh
        subAccountBalances &&
        selectedSubAccountCashValue &&
        subAccountBalances[tempSubId.toString()] &&
        subAccountBalances[tempSubId.toString()].Balances.CashBalance !== selectedSubAccountCashValue
      ) {
        setSubAccountId(-1);
        setTimeout(() => setSubAccountId(tempSubId), 500);
      } else if (positions) {
        // Checking if positions have been journaled into/away from the selected subaccount
        const fetchedSelectedSubPos = positions.filter(p => p.SubaccountId && p.SubaccountId === tempSubId);
        if (fetchedSelectedSubPos.length !== selectedSubAccountPositions.length) {
          setSubAccountId(-1);
          setTimeout(() => setSubAccountId(tempSubId), 500);
        }
      }
    }
  }, [subAccountBalances, positions]);

  useEffect(() => {
    if (!hasSubAccounts && subAccountFilterId) {
      dispatch(UIActions.setSubAccountFilterId({ id: -1 }));
      setSubAccountId(-1);
    }
  }, [hasSubAccounts]);

  useEffect(() => {
    if (accountApi && selectedSubAccountId && !subId) {
      getSubAccountPositionsAndQuotes();
    }
  }, [selectedSubAccountId, accountApi, subId, orders, quoteState]);

  return (
    <>
      {!subId && hasSubAccounts && (
        <div>
          <SubAccountFilter setSubAccountId={setSubAccountId} className="sub-account-filter-position-override" />
        </div>
      )}
      <div className="block-section">
        <div className="row">
          <BlockColumn
            title="Account value"
            value={selectedSubAccountTotalValue === 0 ? 0 : selectedSubAccountTotalValue || accountValue}
            isLoading={isFetchingBalances || isFetchingPositions}
          />
          <BlockColumn
            title="Position value"
            value={selectedSubAccountPositionValue === 0 ? 0 : selectedSubAccountPositionValue || positionValue}
            isLoading={isFetchingBalances || isFetchingPositions}
          />
        </div>
      </div>
      <div className="block-section">
        <div className="row">
          <BlockColumn
            title="Daily P/L"
            value={dailyPL === 0 ? 0 : dailyPL}
            signed
            isLoading={!loaded.isLoaded && isLoading}
          />
        </div>
      </div>
      <div className="block-section">
        <div className="row">
          <BlockColumn
            title="Cash/Cash Alts"
            value={
              selectedSubAccountCashValue === 0
                ? 0
                : selectedSubAccountCashValue || ((totals && totals.cashAndCashAlts) || 0)
            }
            isLoading={isFetchingBalances}
          />
          <BlockColumn
            title="Available for trading"
            value={
              selectedSubAccountBalances
                ? selectedSubAccountBalances.BuyingPower.AvailableFunds === 0
                  ? 0
                  : selectedSubAccountBalances.BuyingPower.AvailableFunds
                : (totals && totals.availableForTrading) || 0
            }
            signed={true}
            hideIcon={true}
            hidePrefix={true}
            isLoading={isFetchingBalances}
          />
        </div>
      </div>
    </>
  );
};
